import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Images, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { fetchListings } from '@/lib/marketplace';
import { coverImageFor, coverCoverageReport, type CoverSource } from '@/lib/coverImage';
import { tradesMissingCoverImage, TRADE_IMAGES, tradeLabel, localizedSlug } from '@/data/constants';
import type { Listing } from '@/components/JobCard';

/**
 * COVER IMAGE AUDIT — live, browser-side proof (not a code review).
 *
 * Runs ON the deployed site against the real published feed:
 *  1. fetches every published listing through the same rate-limited
 *     `browse-listings` function the marketplace uses,
 *  2. resolves each cover with the SAME resolver the cards use
 *     (src/lib/coverImage.ts: upload -> trade -> branded fallback),
 *  3. actually LOADS each resolved URL with an Image() probe, so a dead storage
 *     object or CDN failure is caught here instead of by a contractor looking
 *     at a broken card.
 */

type Row = {
  listing: Listing;
  src: string;
  source: CoverSource;
  ok: boolean | null;
  ms: number | null;
};

/** Resolves true only when the browser can actually decode the bitmap. */
const probe = (url: string): Promise<{ ok: boolean; ms: number }> =>
  new Promise((resolve) => {
    const started = Date.now();
    let settled = false;
    const done = (ok: boolean) => {
      if (settled) return;
      settled = true;
      resolve({ ok, ms: Date.now() - started });
    };
    const img = new Image();
    img.onload = () => done(img.naturalWidth > 1 && img.naturalHeight > 1);
    img.onerror = () => done(false);
    img.src = url;
    setTimeout(() => done(false), 15000);
  });

const AdminCoverAudit: React.FC = () => {
  const { lang, path } = useI18n();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [running, setRunning] = useState(false);
  const [assetsOk, setAssetsOk] = useState<number | null>(null);

  const run = async () => {
    setRunning(true);
    setRows(null);
    setAssetsOk(null);

    const listings = await fetchListings({ limit: 500 });

    const resolved: Row[] = listings.map((l) => {
      const c = coverImageFor(l);
      return { listing: l, src: c.src, source: c.source, ok: null, ms: null };
    });
    setRows(resolved);

    const checked = await Promise.all(
      resolved.map(async (r) => {
        const { ok, ms } = await probe(r.src);
        return { ...r, ok, ms };
      }),
    );
    setRows(checked);

    // Probe the library itself, so a missing CDN asset is caught even when no
    // listing currently uses that trade.
    const assets = await Promise.all(Object.values(TRADE_IMAGES).map((u) => probe(u)));
    setAssetsOk(assets.filter((a) => a.ok).length);
    setRunning(false);
  };

  const missing = tradesMissingCoverImage();
  const report = coverCoverageReport();

  const total = rows?.length ?? 0;
  const bySource = (s: CoverSource) => rows?.filter((r) => r.source === s).length ?? 0;
  const failures = rows?.filter((r) => r.ok === false) ?? [];
  const verified = rows?.filter((r) => r.ok === true).length ?? 0;
  const allGood = rows !== null && !running && failures.length === 0 && total > 0;

  const stats = [
    { label: 'Published projects', value: String(total), tone: 'text-slate-800' },
    { label: 'Homeowner upload', value: String(bySource('upload')), tone: 'text-emerald-700' },
    { label: 'Trade / category', value: String(bySource('trade')), tone: 'text-blue-700' },
    { label: 'Branded fallback', value: String(bySource('fallback')), tone: 'text-amber-700' },
    {
      label: 'Loaded OK',
      value: `${verified}/${total}`,
      tone: failures.length === 0 ? 'text-emerald-700' : 'text-red-700',
    },
  ];

  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-black text-slate-900">
            <Images className="h-5 w-5 text-brand-orange" /> Cover image audit
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Runs on the live site: resolves every published project's cover and loads each URL in
            this browser. Chain: homeowner upload → trade/category cover → branded marketplace
            cover.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={running}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-navy px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-800 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${running ? 'animate-spin' : ''}`} />
          {running ? 'Verifying…' : 'Run live audit'}
        </button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div
          className={`rounded-xl border p-3 text-sm ${
            report.ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
          }`}
        >
          <p className="flex items-center gap-2 font-bold text-slate-900">
            {report.ok ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-red-600" />
            )}
            tradesMissingCoverImage()
          </p>
          <code className="mt-1 block break-all text-xs text-slate-700">
            {missing.length === 0 ? '[]  // every trade has a cover photo' : JSON.stringify(missing)}
          </code>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
          <p className="font-bold text-slate-900">Library</p>
          <p className="mt-1 text-xs text-slate-700">
            {Object.keys(TRADE_IMAGES).length} trade covers + 1 branded marketplace fallback
            {assetsOk !== null && (
              <span
                className={
                  assetsOk === Object.keys(TRADE_IMAGES).length
                    ? ' font-bold text-emerald-700'
                    : ' font-bold text-red-700'
                }
              >
                {' '}
                · {assetsOk}/{Object.keys(TRADE_IMAGES).length} assets decoded
              </span>
            )}
          </p>
        </div>
      </div>

      {rows === null ? (
        <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
          No audit run yet in this session. Click <strong>Run live audit</strong> to verify the
          deployed feed.
        </p>
      ) : (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  {s.label}
                </p>
                <p className={`mt-1 text-2xl font-black ${s.tone}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {allGood && (
            <p className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm font-bold text-emerald-800 ring-1 ring-emerald-200">
              <CheckCircle2 className="h-4 w-4" />
              All {total} published projects resolved a cover and every image decoded in this
              browser. Zero gray placeholders, zero broken images.
            </p>
          )}

          {failures.length > 0 && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3">
              <p className="flex items-center gap-2 text-sm font-bold text-red-800">
                <AlertTriangle className="h-4 w-4" /> {failures.length} cover(s) failed to load
              </p>
              <ul className="mt-2 space-y-1 text-xs text-red-900">
                {failures.map((f) => (
                  <li key={f.listing.id} className="break-all">
                    <strong>{f.listing.title}</strong> ({f.source}) — {f.src}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-red-800">
                Cards still render a photo: CoverImage degrades upload → trade → branded at
                runtime. Fix the source URL to restore the poster's own image.
              </p>
            </div>
          )}

          {/* Visual grid — the on-site screenshot surface. */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {rows.map((r) => (
              <Link
                key={r.listing.id}
                to={path(`/jobs/${localizedSlug(r.listing.slug, r.listing.trade, lang)}`)}
                className="group overflow-hidden rounded-xl border border-slate-200 bg-white"
                title={r.src}
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-200">
                  <img
                    src={r.src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-center"
                  />
                  <span
                    className={`absolute left-1 top-1 rounded px-1.5 py-0.5 text-[9px] font-black uppercase text-white ${
                      r.source === 'upload'
                        ? 'bg-emerald-600'
                        : r.source === 'trade'
                        ? 'bg-blue-600'
                        : 'bg-amber-600'
                    }`}
                  >
                    {r.source}
                  </span>
                  {r.ok === false && (
                    <span className="absolute inset-x-0 bottom-0 bg-red-600 px-1 py-0.5 text-[9px] font-bold text-white">
                      LOAD FAILED
                    </span>
                  )}
                </div>
                <div className="p-2">
                  <p className="truncate text-[11px] font-bold text-slate-800 group-hover:text-brand-orange">
                    {r.listing.title}
                  </p>
                  <p className="text-[10px] text-slate-500">
                    {tradeLabel(r.listing.trade, lang)}
                    {r.ms !== null ? ` · ${r.ms}ms` : ''}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default AdminCoverAudit;
